import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/textarea?tab=designer).
 *
 * @slot default - Slot para texto adicional ou instruções a serem exibidos junto ao textarea.
 */
@Component({
  tag: 'br-textarea',
  styleUrl: 'textarea.scss',
  shadow: true,
  formAssociated: true,
})
export class Textarea {
  @Element() el: HTMLBrTextareaElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Valor exibido no textarea.
   * Pode ser alterado pelo usuário se a propriedade `readonly` não estiver ativa.
   */
  @Prop({ mutable: true, reflect: true }) value: string

  /**
   * Texto exibido como rótulo do input.
   */
  @Prop({ reflect: true }) readonly label: string

  /**
   * Texto exibido dentro do input quando está vazio, fornecendo uma dica ou sugestão ao usuário.
   */
  @Prop({ reflect: true }) readonly placeholder: string

  /**
   * Texto alternativo para acessibilidade quando não há label visível.
   * Fornece um rótulo para tecnologias assistivas sem exibir visualmente.
   */
  @Prop({ reflect: true }) readonly ariaLabel: string

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId('br-textarea')

  /**
   * Indica se o textarea está desabilitado. Quando verdadeiro, o usuário não pode interagir com o campo.
   */
  @Prop({ reflect: true }) readonly disabled: boolean = false

  /**
   * Número máximo de caracteres permitidos no textarea. Se definido como 0, não há limite.
   */
  @Prop({ reflect: true }) readonly maxlength: number = 0

  /**
   * Mostra o contador com a quantidade máxima de caracteres.
   */
  @Prop({ reflect: true }) readonly showCounter: boolean = false

  /**
   * Define o estado visual do componente, podendo ser 'danger', 'success' ou 'warning'.
   */
  @Prop({ reflect: true }) readonly state: 'danger' | 'success' | 'warning'
  /**
   * Ajusta a densidade do componente, podendo ser 'small', 'medium' ou 'large'.
   */
  @Prop({ reflect: true }) readonly density: 'small' | 'medium' | 'large' = 'medium'

  /**
   * Se verdadeiro, o rótulo e o input estarão na mesma linha (layout inline).
   */
  @Prop({ reflect: true }) readonly isInline: boolean = false

  /**
   * Número de linhas visíveis no textarea.
   * Define a altura do componente em relação ao número de linhas de texto exibidas.
   */
  @Prop({ reflect: true }) readonly rows: number

  /**
   * Número de colunas (caracteres) visíveis no textarea.
   * Define a largura do componente em relação ao número de caracteres por linha.
   */
  @Prop({ reflect: true }) readonly cols: number

  /**
   * Comprimento atual do texto no textarea. É atualizado sempre que o valor do textarea muda.
   */
  @State() currentLength: number

  /**
   * Indica se o slot padrão possui conteúdo.
   */
  @State() hasDefaultSlot: boolean = false

  componentWillLoad() {
    this.currentLength = this.value?.length || 0
    this.checkSlotContent()
  }

  private checkSlotContent() {
    this.hasDefaultSlot = Array.from(this.el.childNodes).some((node) => {
      // Ignora nós que têm atributo slot (pertencem a outros slots)
      if (node.nodeType === Node.ELEMENT_NODE && (node as Element).hasAttribute('slot')) {
        return false
      }
      // Verifica se é elemento ou texto não vazio
      return (
        node.nodeType === Node.ELEMENT_NODE || (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '')
      )
    })
  }

  /**
   * Valor atualizado do textarea
   */
  @Event() valueChange: EventEmitter<string>

  @Watch('value')
  valueChanged() {
    this.currentLength = this.value?.length || 0
  }

  private readonly handleInput = (event: Event) => {
    this.value = (event.target as HTMLInputElement).value
    this.currentLength = this.value?.length || 0
    this.valueChange.emit(this.value)
  }

  /**
   * Define um novo valor para o textarea.
   * @param newValue - O novo valor a ser definido.
   */
  @Method()
  async setValue(newValue: string) {
    this.value = newValue
    this.currentLength = this.value?.length || 0
    this.valueChange.emit(this.value)
  }

  /**
   * Retorna os ids para o atributo aria-describedby do textarea
   */
  private getDescribedBy(): string {
    return [this.showCounter ? `${this.customId}-limitMax` : null, `${this.customId}-slotInfo`]
      .filter(Boolean)
      .join(' ')
  }

  /**
   * Determina o texto a ser usado para acessibilidade quando não há label
   * Ordem de prioridade:
   *  - aria-label do elemento host
   *  - propriedade ariaLabel do componente
   *  - placeholder
   *  - texto padrão ("Campo de texto")
   */
  private getAccessibilityLabel(): string {
    const hostAriaLabel = this.el.getAttribute('aria-label')

    return hostAriaLabel && hostAriaLabel.trim() !== ''
      ? hostAriaLabel
      : this.ariaLabel && this.ariaLabel.trim() !== ''
        ? this.ariaLabel
        : this.placeholder && this.placeholder.trim() !== ''
          ? this.placeholder
          : 'Campo de texto'
  }

  private getCssClassMap(): CssClassMap {
    return {
      'br-textarea': true,
      small: this.density === 'small',
      medium: this.density === 'medium',
      large: this.density === 'large',
      success: this.state === 'success',
      danger: this.state === 'danger',
      warning: this.state === 'warning',
    }
  }

  render() {
    const caracteresRestantes: number = this.maxlength - this.currentLength
    const mostrarLimite = this.currentLength > 0
    const hasLabel = !!this.label

    return (
      <Host>
        <div class={this.getCssClassMap()}>
          {this.isInline ? (
            <div class="row">
              {hasLabel && (
                <div class="col-auto pt-half">
                  <label htmlFor={this.customId}>{this.label}</label>
                </div>
              )}
              <div class="col">
                <textarea
                  id={this.customId}
                  value={this.value}
                  placeholder={this.placeholder}
                  disabled={this.disabled}
                  onInput={this.handleInput}
                  {...(this.rows > 0 ? { rows: this.rows } : {})}
                  {...(this.cols > 0 ? { cols: this.cols } : {})}
                  {...(this.maxlength > 0 ? { maxLength: this.maxlength } : {})}
                  aria-describedby={this.getDescribedBy()}
                  {...(!hasLabel ? { 'aria-label': this.getAccessibilityLabel() } : {})}
                ></textarea>
                {this.showCounter && (
                  <div>
                    {mostrarLimite ? (
                      <span class="restantes" aria-live="polite" role="status" id={`${this.customId}-limitMax`}>
                        Restam <strong>{caracteresRestantes}</strong> caracteres
                      </span>
                    ) : (
                      <span class="limit" aria-live="polite" id={`${this.customId}-limitMax`}>
                        Limite máximo de <strong>{this.maxlength}</strong> caracteres
                      </span>
                    )}
                  </div>
                )}
                {this.hasDefaultSlot && (
                  <div id={`${this.customId}-slotInfo`} aria-live="polite">
                    <slot></slot>
                  </div>
                )}
              </div>
            </div>
          ) : (
            [
              hasLabel ? <label htmlFor={this.customId}>{this.label}</label> : null,
              <textarea
                id={this.customId}
                value={this.value}
                placeholder={this.placeholder}
                disabled={this.disabled}
                onInput={this.handleInput}
                {...(this.rows > 0 ? { rows: this.rows } : {})}
                {...(this.cols > 0 ? { cols: this.cols } : {})}
                {...(this.maxlength > 0 ? { maxLength: this.maxlength } : {})}
                aria-describedby={this.getDescribedBy()}
                {...(!hasLabel ? { 'aria-label': this.getAccessibilityLabel() } : {})}
              ></textarea>,
              this.showCounter ? (
                <div>
                  {mostrarLimite ? (
                    <span class="restantes" aria-live="polite" role="status" id={`${this.customId}-limitMax`}>
                      Restam <strong>{caracteresRestantes}</strong> caracteres
                    </span>
                  ) : (
                    <span class="limit" aria-live="polite" id={`${this.customId}-limitMax`}>
                      Limite máximo de <strong>{this.maxlength}</strong> caracteres
                    </span>
                  )}
                </div>
              ) : null,
              this.hasDefaultSlot ? (
                <div id={`${this.customId}-slotInfo`} aria-live="polite">
                  <slot></slot>
                </div>
              ) : null,
            ]
          )}
        </div>
      </Host>
    )
  }
}
